import React, { useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import { Shield } from 'lucide-react';

const SECTIONS = [
  { id: 'introduction',       title: '1. Introduction' },
  { id: 'information-collect',title: '2. Information We Collect' },
  { id: 'how-we-use',         title: '3. How We Use Your Information' },
  { id: 'sharing',            title: '4. Third-Party Services & Sharing' },
  { id: 'ai-processing',      title: '5. AI Processing' },
  { id: 'cookies',            title: '6. Cookies and Tracking' },
  { id: 'content-moderation', title: '7. Content Moderation' },
  { id: 'ugc-deletion',       title: '8. User Content & Account Deletion' },
  { id: 'data-retention',     title: '9. Data Retention' },
  { id: 'security',           title: '10. Security & Breach Notification' },
  { id: 'your-rights',        title: '11. Your Rights' },
  { id: 'ccpa',               title: '12. California Privacy Rights (CCPA)' },
  { id: 'children',           title: '13. Children\'s & Teens\' Privacy' },
  { id: 'third-party',        title: '14. Third-Party Links' },
  { id: 'changes',            title: '15. Changes to This Policy' },
  { id: 'contact',            title: '16. Contact Us' },
];

export default function PrivacyPolicyPage() {
  const { theme } = useTheme();
  const { setBreadcrumbItems } = useBreadcrumb();

  useEffect(() => {
    setBreadcrumbItems([{ label: 'Privacy Policy', path: '/privacy' }]);
    return () => setBreadcrumbItems([]);
  }, [setBreadcrumbItems]);

  const SectionHeading = ({ id, title }) => (
    <h2 id={id} className={`scroll-mt-28 text-xl font-bold ${theme.text} mt-10 mb-3 pb-2 border-b ${theme.border}`}>
      {title}
    </h2>
  );

  const P = ({ children }) => (
    <p className={`${theme.textSecondary} leading-relaxed mb-4`}>{children}</p>
  );

  const UL = ({ items }) => (
    <ul className={`list-disc list-inside ${theme.textSecondary} space-y-2 mb-4 ml-2`}>
      {items.map((item, i) => <li key={i} className="leading-relaxed">{item}</li>)}
    </ul>
  );

  const SubHeading = ({ children }) => (
    <h3 className={`text-base font-semibold ${theme.text} mt-6 mb-2`}>{children}</h3>
  );

  const InfoBox = ({ children }) => (
    <div className={`${theme.backgroundSecondary} border ${theme.border} rounded-lg p-4 mb-4`}>
      <p className={`${theme.textSecondary} text-sm leading-relaxed`}>{children}</p>
    </div>
  );

  return (
    <div className={`min-h-screen ${theme.background}`}>
      {/* Header */}
      <div className={`${theme.background} py-4`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className={`${theme.card} border ${theme.border} rounded-xl px-6 py-4 flex items-center gap-3`}>
            <Shield size={22} className={theme.link} />
            <div>
              <p className={`text-lg font-bold leading-tight ${theme.text}`}>Privacy Policy</p>
              <p className={`text-xs leading-tight ${theme.textSecondary}`}>Last updated: May 29, 2026</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 flex gap-8">
        {/* Sidebar TOC — desktop */}
        <aside className="hidden lg:block w-56 flex-shrink-0">
          <div className={`sticky ${theme.card} border ${theme.border} rounded-xl p-4`} style={{ top: '6rem' }}>
            <p className={`text-xs font-semibold uppercase tracking-wider ${theme.textSecondary} mb-3`}>Contents</p>
            <nav className="space-y-0.5">
              {SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className={`block px-2 py-1.5 rounded-lg text-xs transition-colors no-underline ${theme.textSecondary} ${theme.cardHover}`}
                >
                  {s.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main content */}
        <div className={`flex-1 min-w-0 ${theme.card} border ${theme.border} rounded-xl p-6 md:p-8`}>
          <P>
            Kynaix LLC ("Kynaix," "we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the Kynaix Sports platform ("Service"). Please read this policy carefully before using our Service.
          </P>

          {/* ── 1. Introduction ────────────────────────────────────────── */}
          <SectionHeading id="introduction" title="1. Introduction" />
          <P>This Privacy Policy applies to all users of Kynaix Sports, including visitors to our website, users of our iOS and Android mobile applications, and anyone who interacts with our services in any other way. By using Kynaix Sports, you consent to the practices described in this policy.</P>
          <P>This policy should be read alongside our <a href="/cookies" className={`${theme.link} underline`}>Cookie Policy</a> and our <a href="/terms" className={`${theme.link} underline`}>Terms of Service</a>. If you have questions, see <a href="#contact" className={`${theme.link} underline`}>Section 16 (Contact Us)</a>.</P>

          {/* ── 2. Information We Collect ──────────────────────────────── */}
          <SectionHeading id="information-collect" title="2. Information We Collect" />

          <SubHeading>Information You Provide Directly</SubHeading>
          <UL items={[
            'Account information: full name, email address, username, password, phone number (optional), date of birth',
            'Profile information: profile photo, bio, city/location, sports preferences, skill level, playing position',
            'Communications: messages, posts, comments, squad discussions, marketplace conversations, and other content you submit',
            'Sports activity: game results, tournament registrations, event attendance, team and squad memberships, match history, scores, and performance statistics',
            'Financial data: group expense records, contribution amounts, settle-up transactions, and payment information for marketplace purchases (payment card processing is handled by our payment partners — we do not store raw card numbers)',
            'Uploaded documents: court booking permits, facility reservations, and other PDF documents you upload to the platform',
            'Support requests: information you provide when contacting our support team',
          ]} />

          <SubHeading>Information Collected Automatically</SubHeading>
          <UL items={[
            'Device information: IP address, browser type, operating system, device identifiers, and mobile app version',
            'Usage data: screens and pages visited, features used, time spent on the platform, tap and click patterns, and session duration',
            'Precise location: GPS-level latitude, longitude, and accuracy when you grant location permission (used for nearby events, marketplace listings, and squad discovery). You may deny or revoke this permission at any time in your device settings',
            'Push notification tokens: device-specific tokens (FCM tokens) used to deliver push notifications to your iOS or Android device',
            'Cookies and tracking technologies: see Section 6 for details',
          ]} />

          <SubHeading>Information from Third-Party Login Providers</SubHeading>
          <UL items={[
            'Google Sign-In: your name, email address, and profile picture from your Google account',
            'Facebook Login: your Facebook user ID, name, email address, and profile picture',
            'Sign in with Apple (iOS): your name and email address (Apple may provide a relay email address)',
          ]} />

          {/* ── 3. How We Use ──────────────────────────────────────────── */}
          <SectionHeading id="how-we-use" title="3. How We Use Your Information" />
          <P>We use the information we collect to:</P>
          <UL items={[
            'Provide, maintain, and improve the Kynaix Sports platform',
            'Create and manage your account and profile',
            'Process transactions and send related notifications',
            'Deliver push notifications you have opted into (squad invites, game reminders, activity updates)',
            'Send transactional emails such as account verification, password resets, and activity notifications',
            'Send marketing communications (with your consent where required by law)',
            'Match you with nearby players, squads, events, and marketplace listings using your location',
            'Maintain sport participation records, tournament history, and game statistics',
            'Process and parse uploaded permit documents using AI to extract event details (see Section 5)',
            'Monitor platform content for safety through automated and human moderation (see Section 7)',
            'Respond to your comments, questions, and requests',
            'Produce aggregate, anonymized analytics to understand platform usage and improve the user experience',
            'Detect and prevent fraud, abuse, spam, and other prohibited activities',
            'Comply with legal obligations and enforce our Terms of Service',
          ]} />

          {/* ── 4. Sharing / Third Parties ─────────────────────────────── */}
          <SectionHeading id="sharing" title="4. Third-Party Services & Sharing" />
          <P>We do not sell your personal information. The following third-party services process data on our behalf as data processors. Each is contractually required to handle your data in accordance with applicable privacy laws and our data processing agreements.</P>

          <SubHeading>Infrastructure & Storage</SubHeading>
          <UL items={[
            'Amazon Web Services (AWS) — cloud hosting, database, and file storage. User data, uploaded photos, profile images, and permit documents are stored on AWS servers located in the United States.',
          ]} />

          <SubHeading>Messaging</SubHeading>
          <UL items={[
            'Stream (stream-io) — powers real-time messaging between users in squads and the marketplace. Message content, sender/recipient identifiers, and timestamps are processed by Stream on servers located in the United States. Stream\'s privacy policy is available at getstream.io/privacy-policy.',
          ]} />

          <SubHeading>Push Notifications</SubHeading>
          <UL items={[
            'Google Firebase Cloud Messaging (FCM) — delivers push notifications to Android and iOS devices. Your device push token and notification payload are transmitted through Google\'s FCM infrastructure.',
            'Apple Push Notification Service (APNs) — used by Firebase on iOS devices to deliver push notifications.',
          ]} />

          <SubHeading>Authentication</SubHeading>
          <UL items={[
            'Google OAuth — if you sign in with Google, authentication is handled by Google.',
            'Facebook Login — if you sign in with Facebook, authentication is handled by Meta.',
            'Apple Sign-In — if you sign in with Apple on iOS, authentication is handled by Apple.',
          ]} />

          <SubHeading>AI & Document Processing</SubHeading>
          <UL items={[
            'Anthropic (Claude AI) — permit PDFs you upload are sent to Anthropic\'s API to extract event details such as dates, times, and locations. See Section 5 for full details.',
          ]} />

          <SubHeading>Analytics & Observability</SubHeading>
          <UL items={[
            'Google Analytics (GA4) — we use Google Analytics to collect aggregate, anonymized data about how users interact with the platform (page views, feature usage, session counts). We do not send personally identifiable information to Google Analytics. Individual users are not identifiable in our analytics reports.',
            'Grafana Cloud — we use Grafana Faro for application performance monitoring. This collects aggregate error counts, page load times, crash reports, and session-level telemetry. Data is stored in Grafana\'s US-based infrastructure. We do not send personally identifiable user data to Grafana.',
          ]} />

          <SubHeading>Address Lookup</SubHeading>
          <UL items={[
            'Google Places API — when you search for or enter a location (for events, marketplace listings, or your profile), your search query is sent to Google\'s Places API to provide autocomplete suggestions.',
          ]} />

          <SubHeading>Other Disclosures</SubHeading>
          <UL items={[
            'Other users — your public profile, posts, squad activity, event participation, and marketplace listings are visible to other users as described in your privacy settings',
            'Law enforcement or regulators — when required by law, court order, or to protect the rights, property, or safety of Kynaix, our users, or others',
            'Successor entities — in the event of a merger, acquisition, bankruptcy, or sale of assets, your information may be transferred as part of that transaction. We will notify you before your data becomes subject to a different privacy policy',
          ]} />

          {/* ── 5. AI Processing ────────────────────────────────────────── */}
          <SectionHeading id="ai-processing" title="5. AI Processing" />
          <InfoBox>
            When you upload a court booking permit or facility reservation PDF, the document is transmitted to Anthropic's Claude AI API to automatically extract event details (dates, times, locations, permit numbers). The extracted data is returned to you for review before any events are created.
          </InfoBox>
          <P>Specifically:</P>
          <UL items={[
            'The full PDF document is sent to Anthropic\'s API servers for processing',
            'Anthropic processes the document to extract structured event data only',
            'The processed document is stored on our AWS servers and linked to your account',
            'Anthropic\'s use of data submitted via API is governed by their API usage policy and privacy policy (anthropic.com/privacy)',
            'You can view and delete uploaded permit documents from your account settings',
          ]} />
          <P>We do not use your uploaded documents to train AI models. If you do not wish your documents to be processed by AI, do not use the "Import from PDF" feature — you can create events manually instead.</P>

          {/* ── 6. Cookies ──────────────────────────────────────────────── */}
          <SectionHeading id="cookies" title="6. Cookies and Tracking" />
          <P>We use cookies and similar tracking technologies to enhance your experience on Kynaix Sports. See our <a href="/cookies" className={`${theme.link} underline`}>Cookie Policy</a> for detailed information about the types of cookies we use and how you can control them.</P>
          <P>In summary, we use:</P>
          <UL items={[
            'Necessary cookies: required for the platform to function correctly (authentication, session management)',
            'Preference cookies: remember your settings such as theme and notification preferences',
            'Analytics cookies: Google Analytics GA4 cookies that help us understand aggregate usage patterns. No personally identifiable information is sent',
            'Marketing cookies: used to deliver relevant advertisements (only with your explicit consent)',
          ]} />
          <P>You can manage your cookie preferences at any time via our cookie consent settings. On mobile apps, equivalent tracking is governed by your device\'s app tracking settings (iOS: Settings → Privacy → Tracking; Android: Settings → Privacy → Ads).</P>

          {/* ── 7. Content Moderation ───────────────────────────────────── */}
          <SectionHeading id="content-moderation" title="7. Content Moderation" />
          <P>Kynaix Sports uses both automated systems and human review to keep the platform safe. By using the Service, you acknowledge that:</P>
          <UL items={[
            'Images and videos you upload may be scanned by automated systems to detect prohibited content (nudity, violence, hate symbols, spam)',
            'Messages and chat content may be reviewed by automated systems for policy violations',
            'Content flagged by automated systems or reported by other users may be reviewed by Kynaix staff',
            'We may access, preserve, and disclose any content if we believe it is necessary to comply with legal obligations, enforce our policies, or protect the rights and safety of users',
          ]} />
          <P>Moderation actions (content removal, account suspension) are taken in accordance with our <a href="/terms" className={`${theme.link} underline`}>Terms of Service</a>. You may appeal moderation decisions by contacting <a href="mailto:support@kynaixsports.com" className={`${theme.link} underline`}>support@kynaixsports.com</a>.</P>

          {/* ── 8. UGC & Account Deletion ───────────────────────────────── */}
          <SectionHeading id="ugc-deletion" title="8. User-Generated Content & Account Deletion" />

          <SubHeading>What Happens When You Delete Your Account</SubHeading>
          <P>When you delete your account, the following applies:</P>
          <UL items={[
            'Your personal identifiers (name, email address, username, profile photo, contact information, and login credentials) are permanently deleted within 30 days',
            'Content you created — posts, comments, squad discussions, and marketplace messages — remains on the platform but is permanently dissociated from your identity. Your name and profile are replaced with "Anonymous User." This is consistent with how platforms like Reddit and Discord handle account deletion',
            'Sport participation records (games played, tournament results, scores, match history) are retained permanently even after account deletion. These records form part of the historical integrity of league and tournament data and cannot be removed. You are identified in these records only by an anonymized identifier — your name and contact information are not retained',
            'Marketplace listings you created are removed or anonymized at account deletion',
            'Uploaded permit documents are deleted within 30 days of account deletion',
            'Push notification tokens are immediately deregistered at account deletion',
          ]} />

          <InfoBox>
            Important: If you have personally identifiable information within the body of a post or message (for example, you typed your phone number in a chat message), that content may remain on the platform in anonymized form. We cannot automatically detect and remove PII embedded in free-text content. Please contact us at support@kynaixsports.com if you need specific content removed.
          </InfoBox>

          <SubHeading>Tournament & Game Record Retention</SubHeading>
          <P>By participating in tournaments, leagues, and organized games on Kynaix Sports, you acknowledge and agree that your participation records (games played, outcomes, statistics, and team affiliations) are part of the permanent historical record of those competitions. These records are retained indefinitely to preserve the integrity of sport history on the platform, consistent with how physical record-keeping works in organized sports. This is disclosed at account creation in our <a href="/terms" className={`${theme.link} underline`}>Terms of Service</a>.</P>

          {/* ── 9. Data Retention ───────────────────────────────────────── */}
          <SectionHeading id="data-retention" title="9. Data Retention" />
          <P>We retain your personal information according to the following schedule:</P>
          <UL items={[
            'Active account data: retained for the duration of your account plus 5 years of inactivity before archival or deletion. An account is considered inactive if you have not logged in for 5 consecutive years',
            'Account deletion: personal identifiers are deleted within 30 days of your deletion request. Sport participation records are retained permanently in anonymized form (see Section 8)',
            'Messages and chat history: retained for the life of the conversation in Stream Chat, subject to Stream\'s retention policies. When your account is deleted, your identity is anonymized but message content may remain visible to other participants',
            'Uploaded documents (permit PDFs): retained while your account is active and deleted within 30 days of account deletion',
            'Financial/expense records: retained for 7 years from the date of the transaction to comply with financial record-keeping requirements',
            'Push notification tokens: immediately removed upon account deletion or when you disable notifications',
            'Analytics data: Google Analytics and Grafana data is aggregate and anonymized — it does not contain personal identifiers and is retained per those services\' standard retention schedules',
            'Certain information may be retained longer where required by law, court order, or for legitimate purposes such as fraud prevention and legal defense',
          ]} />

          {/* ── 10. Security & Breach ────────────────────────────────────── */}
          <SectionHeading id="security" title="10. Data Security & Breach Notification" />

          <SubHeading>Security Measures</SubHeading>
          <P>We implement industry-standard security measures to protect your information, including:</P>
          <UL items={[
            'Encryption in transit: all data transmitted between your device and our servers uses TLS/HTTPS',
            'Encrypted password storage: passwords are hashed using industry-standard algorithms and are never stored in plaintext',
            'Access controls: access to production systems and user data is restricted to authorized personnel on a need-to-know basis',
            'Cloud security: AWS infrastructure with security groups, VPC isolation, and S3 bucket policies',
            'Regular security reviews of our codebase and infrastructure',
          ]} />
          <P>However, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee the absolute security of your data.</P>

          <SubHeading>Data Breach Notification</SubHeading>
          <P>In the event of a data breach that is likely to result in a risk to your rights and freedoms, we will:</P>
          <UL items={[
            'Notify relevant supervisory authorities within 72 hours of becoming aware of the breach, as required by applicable law (including GDPR where applicable)',
            'Notify affected users without undue delay — and within 30 days at the latest — by email to the address on your account, describing the nature of the breach, what data was affected, likely consequences, and steps we are taking',
            'For California residents: notify you in accordance with California\'s data breach notification law (Cal. Civ. Code § 1798.29 / § 1798.82) when unencrypted personal information has been, or is reasonably believed to have been, acquired by an unauthorized person',
            'Maintain a record of all data breaches, including those not requiring notification',
          ]} />
          <P>To report a suspected security vulnerability, please contact us at <a href="mailto:support@kynaixsports.com" className={`${theme.link} underline`}>support@kynaixsports.com</a> with "Security Vulnerability" in the subject line.</P>

          {/* ── 11. Your Rights ──────────────────────────────────────────── */}
          <SectionHeading id="your-rights" title="11. Your Rights" />
          <P>Depending on your location, you may have the following rights regarding your personal information:</P>
          <UL items={[
            'Access: request a copy of the personal data we hold about you',
            'Correction: request correction of inaccurate or incomplete data',
            'Deletion: request deletion of your personal data ("right to be forgotten") — subject to the sport participation record retention described in Section 8',
            'Portability: request a copy of your data in a machine-readable format (JSON)',
            'Restriction: request that we restrict processing of your data while a dispute is being resolved',
            'Objection: object to processing of your data for certain purposes, including direct marketing',
            'Withdrawal of consent: withdraw consent for marketing communications at any time via the unsubscribe link in any email or by contacting us',
          ]} />
          <P>To exercise any of these rights, contact us at <a href="mailto:support@kynaixsports.com" className={`${theme.link} underline`}>support@kynaixsports.com</a>. We will acknowledge your request within 5 business days and respond in full within 30 days (extendable by 60 days for complex requests, with notice).</P>
          <P>To request deletion of your account and all associated data, visit our <a href="/data-deletion" className={`${theme.link} underline`}>Account & Data Deletion page</a>.</P>

          <SubHeading>EU/UK Users — GDPR Rights</SubHeading>
          <P>If you are located in the European Economic Area (EEA) or United Kingdom, you have additional rights under the General Data Protection Regulation (GDPR) / UK GDPR:</P>
          <UL items={[
            'Lawful basis: we process your data on the basis of contract performance (to provide the Service), legitimate interests (platform safety, fraud prevention, analytics), and consent (marketing, cookies). You may request details of the specific basis for any processing activity',
            'Right to lodge a complaint: you have the right to lodge a complaint with your local data protection supervisory authority (e.g., the ICO in the UK, or your national DPA in the EU)',
            'International transfers: your data is stored and processed in the United States. Transfers from the EEA/UK to the US are made under appropriate safeguards, including Standard Contractual Clauses (SCCs) where required',
            'Automated decision-making: we do not make solely automated decisions that produce legal or similarly significant effects on you. Feed ranking and match recommendations are algorithmic but do not affect your legal rights',
          ]} />

          <SubHeading>Facebook Login Data Deletion</SubHeading>
          <P>If you signed in using Facebook, we store your Facebook user ID, name, email address, and profile picture. To request deletion of this data:</P>
          <UL items={[
            'Email us at support@kynaixsports.com with subject line "Facebook Data Deletion Request"',
            'Include the email address associated with your Kynaix Sports account',
            'We will permanently delete all Facebook-linked data within 30 days and confirm via email',
          ]} />
          <P>You can also remove Kynaix Sports from your Facebook connected apps at any time by visiting Facebook Settings → Apps and Websites → Remove.</P>

          {/* ── 12. CCPA ─────────────────────────────────────────────────── */}
          <SectionHeading id="ccpa" title="12. California Privacy Rights (CCPA / CPRA)" />
          <P>If you are a California resident, the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA) give you specific rights regarding your personal information.</P>

          <SubHeading>We Do Not Sell or Share Your Personal Information</SubHeading>
          <P>Kynaix does not sell your personal information to third parties. Kynaix does not share your personal information with third parties for cross-context behavioral advertising purposes. You do not need to opt out because we do not engage in these practices.</P>

          <SubHeading>Your California Rights</SubHeading>
          <UL items={[
            'Right to Know: you may request that we disclose the categories of personal information we have collected about you, the purposes for collection, and the categories of third parties with whom we share it',
            'Right to Delete: you may request deletion of your personal information, subject to certain exceptions (e.g., legal obligations, sport participation records)',
            'Right to Correct: you may request correction of inaccurate personal information',
            'Right to Limit Use of Sensitive Personal Information: we do not use sensitive personal information (such as precise geolocation) for any purpose other than providing the Service',
            'Right to Non-Discrimination: we will not discriminate against you for exercising your privacy rights',
          ]} />

          <SubHeading>How to Submit a California Privacy Request</SubHeading>
          <P>Submit requests by email to <a href="mailto:support@kynaixsports.com" className={`${theme.link} underline`}>support@kynaixsports.com</a> with "California Privacy Request" in the subject line. We will verify your identity before processing your request. We will respond within 45 days (extendable by an additional 45 days with notice).</P>

          <SubHeading>Authorized Agents</SubHeading>
          <P>You may designate an authorized agent to submit a request on your behalf. We will require written authorization and may verify your identity directly with you.</P>

          {/* ── 13. Children ──────────────────────────────────────────────── */}
          <SectionHeading id="children" title="13. Children's and Teens' Privacy" />

          <SubHeading>Children Under 13 (COPPA)</SubHeading>
          <P>Kynaix Sports is not directed to children under 13 years of age. We do not knowingly collect personal information from children under 13. If we become aware that we have collected data from a child under 13 without verifiable parental consent, we will promptly delete it. If you believe we have inadvertently collected such information, please contact us immediately at <a href="mailto:support@kynaixsports.com" className={`${theme.link} underline`}>support@kynaixsports.com</a>.</P>
          <P>We collect date of birth at account creation. Accounts with a date of birth indicating the user is under 13 are not permitted to register.</P>

          <SubHeading>Teens (Ages 13–17)</SubHeading>
          <P>We recognize that teens use sports platforms and take additional steps to protect younger users:</P>
          <UL items={[
            'Precise location data is not shared with other users — it is used only to show nearby events and content',
            'Direct messaging is available but monitored for safety (see Section 7)',
            'We do not send marketing communications to users under 18 without parental consent where required by law',
            'Parents or guardians may contact us to request access to, correction of, or deletion of their minor child\'s account',
          ]} />

          {/* ── 14. Third-Party Links ─────────────────────────────────────── */}
          <SectionHeading id="third-party" title="14. Third-Party Links" />
          <P>Our Service may contain links to third-party websites and services. We are not responsible for the privacy practices of those third parties. We encourage you to review the privacy policies of any third-party sites you visit. This policy applies only to information collected by Kynaix Sports.</P>

          {/* ── 15. Changes ─────────────────────────────────────────────── */}
          <SectionHeading id="changes" title="15. Changes to This Policy" />
          <P>We may update this Privacy Policy from time to time. When we make material changes, we will:</P>
          <UL items={[
            'Post the updated policy at kynaixsports.com/privacy with a new "Last updated" date',
            'Send an email notification to all registered users',
            'Display an in-app banner for at least 30 days after significant changes',
          ]} />
          <P>Your continued use of the Service after the effective date of any changes constitutes your acceptance of the updated policy. If you do not agree to the changes, you must stop using the Service and may request deletion of your account.</P>

          {/* ── 16. Contact ─────────────────────────────────────────────── */}
          <SectionHeading id="contact" title="16. Contact Us" />
          <P>If you have questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact us:</P>
          <div className={`${theme.backgroundSecondary} border ${theme.border} rounded-lg p-4 mt-2 mb-4`}>
            <p className={`font-semibold ${theme.text} mb-1`}>Kynaix LLC — Privacy Team</p>
            <p className={`${theme.textSecondary} text-sm`}>Email: <a href="mailto:support@kynaixsports.com" className={theme.link}>support@kynaixsports.com</a></p>
            <p className={`${theme.textSecondary} text-sm`}>Subject line: "Privacy Request" for fastest routing</p>
            <p className={`${theme.textSecondary} text-sm mt-2`}>Mailing address:</p>
            <p className={`${theme.textSecondary} text-sm`}>Kynaix LLC</p>
            <p className={`${theme.textSecondary} text-sm`}>c/o ZenBusiness Inc.</p>
            <p className={`${theme.textSecondary} text-sm`}>400 Riverview Plaza Suite 104</p>
            <p className={`${theme.textSecondary} text-sm`}>Trenton, NJ 08611</p>
            <p className={`${theme.textSecondary} text-sm mt-2`}>Website: <a href="https://kynaix.com" className={theme.link}>kynaix.com</a></p>
          </div>

          <P>We aim to acknowledge all privacy-related inquiries within 5 business days. For data deletion requests, account closure, or California/GDPR rights requests, please see the relevant section above for specific timelines.</P>

          <P>Quick links to related policies:</P>
          <UL items={[]} />
          <div className="flex flex-wrap gap-3 mb-6">
            {[
              { label: 'Cookie Policy', href: '/cookies' },
              { label: 'Terms of Service', href: '/terms' },
              { label: 'Request Data Deletion', href: '/data-deletion' },
            ].map(({ label, href }) => (
              <a
                key={href}
                href={href}
                className={`inline-flex items-center px-4 py-2 rounded-lg border ${theme.border} text-sm ${theme.text} ${theme.cardHover} no-underline transition-colors`}
              >
                {label}
              </a>
            ))}
          </div>

          <P>We aim to acknowledge all privacy-related inquiries within 5 business days. For data deletion requests, account closure, or California/GDPR rights requests, please see the relevant section above for specific timelines.</P>

          <P>Quick links to related policies:</P>
          <UL items={[]} />
          <div className="flex flex-wrap gap-3 mb-6">
            {[
              { label: 'Cookie Policy', href: '/cookies' },
              { label: 'Terms of Service', href: '/terms' },
            ].map(({ label, href }) => (
              <a
                key={href}
                href={href}
                className={`inline-flex items-center px-4 py-2 rounded-lg border ${theme.border} text-sm ${theme.text} ${theme.cardHover} no-underline transition-colors`}
              >
                {label}
              </a>
            ))}
          </div>

          <P>We aim to acknowledge all privacy-related inquiries within 5 business days. For data deletion requests, account closure, or California/GDPR rights requests, please see the relevant section above for specific timelines.</P>

          <P>Quick links to related policies:</P>
          <UL items={[]} />
          <div className="flex flex-wrap gap-3 mb-6">
            {[
              { label: 'Cookie Policy', href: '/cookies' },
              { label: 'Terms of Service', href: '/terms' },
            ].map(({ label, href }) => (
              <a
                key={href}
                href={href}
                className={`inline-flex items-center px-4 py-2 rounded-lg border ${theme.border} text-sm ${theme.text} ${theme.cardHover} no-underline transition-colors`}
              >
                {label}
              </a>
            ))}
          </div>

          <div className={`mt-10 pt-6 border-t ${theme.border}`}>
            <p className={`text-xs ${theme.textSecondary} leading-relaxed`}>
              © 2026 Kynaix LLC. All Rights Reserved. All content included on this site is the property of Kynaix LLC or its content suppliers and is protected by United States and international copyright laws.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
